import { C } from '../palette';
import { PixelBuf } from '../pixel';

/**
 * "Bloom", the sprout-hooded courier of Sunrise Vale — an original character drawn
 * procedurally so the game ships without any external art.
 */

export type HeroPose =
  | 'idle'
  | 'walk0'
  | 'walk1'
  | 'walk2'
  | 'run0'
  | 'run1'
  | 'run2'
  | 'jump'
  | 'fall'
  | 'skid'
  | 'hurt'
  | 'dead'
  | 'flag'
  | 'duck';

export const HERO_POSES: HeroPose[] = [
  'idle',
  'walk0',
  'walk1',
  'walk2',
  'run0',
  'run1',
  'run2',
  'jump',
  'fall',
  'skid',
  'hurt',
  'dead',
  'flag',
  'duck',
];

export type HeroForm = 'small' | 'big' | 'fire';

interface Colors {
  skin: string;
  skinDark: string;
  hood: string;
  hoodDark: string;
  tunic: string;
  tunicDark: string;
  belt: string;
  boot: string;
  bootDark: string;
  leaf: string;
  leafDark: string;
  outline: string;
  eye: string;
}

const NORMAL: Colors = {
  skin: C.skin,
  skinDark: C.skinDark,
  hood: C.leaf,
  hoodDark: C.leafDark,
  tunic: C.tunic,
  tunicDark: C.tunicDark,
  belt: C.belt,
  boot: C.boot,
  bootDark: C.bootDark,
  leaf: C.leaf,
  leafDark: C.leafDark,
  outline: C.outline,
  eye: C.eye,
};

const FIRE: Colors = {
  ...NORMAL,
  hood: C.fireTunic,
  hoodDark: C.fireTunicDark,
  tunic: C.fireTunic,
  tunicDark: C.fireTunicDark,
  belt: C.fireTrim,
};

interface Rig {
  w: number;
  h: number;
  headX: number;
  headY: number;
  headW: number;
  headH: number;
  bodyX: number;
  bodyY: number;
  bodyW: number;
  bodyH: number;
  legY: number;
  legH: number;
  armY: number;
  armH: number;
}

const SMALL: Rig = {
  w: 12,
  h: 15,
  headX: 2,
  headY: 1,
  headW: 8,
  headH: 6,
  bodyX: 2,
  bodyY: 7,
  bodyW: 8,
  bodyH: 5,
  legY: 11,
  legH: 4,
  armY: 7,
  armH: 3,
};

const BIG: Rig = {
  w: 14,
  h: 24,
  headX: 3,
  headY: 1,
  headW: 9,
  headH: 8,
  bodyX: 2,
  bodyY: 9,
  bodyW: 10,
  bodyH: 8,
  legY: 16,
  legH: 8,
  armY: 10,
  armH: 5,
};

/** Big form crouching: same width, shorter. */
const DUCK: Rig = { ...BIG, headY: 6, bodyY: 14, bodyH: 6, legY: 20, legH: 4, armY: 14, armH: 4 };

export function heroSprite(form: HeroForm, pose: HeroPose): PixelBuf {
  const c = form === 'fire' ? FIRE : NORMAL;
  let rig = form === 'small' ? SMALL : BIG;
  if (pose === 'duck') {
    if (form === 'small') rig = SMALL; // small form cannot duck; reuse idle
    else rig = DUCK;
  }
  const p = new PixelBuf(rig.w, rig.h);

  // Lean and bob offsets per pose give each animation its own silhouette.
  const lean = pose === 'run0' || pose === 'run1' || pose === 'run2' ? 1 : pose === 'skid' ? -1 : 0;
  const bob = pose === 'walk1' || pose === 'run1' ? 1 : 0;

  drawLegs(p, rig, c, pose);
  drawArmBack(p, rig, c, pose, lean);
  drawBody(p, rig, c, pose, lean, bob);
  drawHead(p, rig, c, pose, lean, bob);
  drawArmFront(p, rig, c, pose, lean, bob);
  return p;
}

function drawHead(p: PixelBuf, r: Rig, c: Colors, pose: HeroPose, lean: number, bob: number): void {
  const x = r.headX + lean;
  const y = r.headY + bob;
  const w = r.headW;
  const h = r.headH;

  // sprout on the hood
  p.rect(x + w - 4, y - 1, 2, 1, c.leaf);
  p.px(x + w - 2, y - 1, c.leafDark);

  // hood shell
  p.blob(x - 1, y - 1, w + 2, h + 1, c.outline);
  p.blob(x, y, w, h - 1, c.hood);
  // face opening
  const fx = x + 2;
  const fy = y + 2;
  p.rect(fx, fy, w - 2, h - 4, c.skin);
  p.rect(fx, fy + h - 5, w - 3, 1, c.skinDark);
  // hood brim
  p.rect(x, y + 1, w, 1, c.hoodDark);
  p.rect(x + w - 1, y + 2, 1, h - 4, c.hoodDark);

  // eye (character always drawn facing right; renderer mirrors when needed)
  const ex = fx + w - 5;
  const ey = fy + 1;
  if (pose === 'dead') {
    p.px(ex, ey, c.outline);
    p.px(ex + 1, ey + 1, c.outline);
    p.px(ex + 1, ey, c.outline);
    p.px(ex, ey + 1, c.outline);
  } else if (pose === 'hurt') {
    p.rect(ex, ey + 1, 2, 1, c.outline);
  } else {
    p.rect(ex, ey, 2, 2, c.outline);
    p.px(ex + 1, ey, c.eye);
  }
  // scarf
  p.rect(x, y + h - 2, w, 1, c.belt);
}

function drawBody(p: PixelBuf, r: Rig, c: Colors, pose: HeroPose, lean: number, bob: number): void {
  const x = r.bodyX + lean;
  const y = r.bodyY + bob;
  p.blob(x - 1, y, r.bodyW + 2, r.bodyH, c.outline);
  p.blob(x, y, r.bodyW, r.bodyH - 1, c.tunic);
  p.rect(x + 1, y + r.bodyH - 3, r.bodyW - 2, 1, c.belt);
  p.rect(x + r.bodyW - 2, y + 1, 1, r.bodyH - 4, c.tunicDark);
  if (pose === 'flag') {
    // gripping the pole: chest pressed forward
    p.rect(x + r.bodyW - 1, y + 1, 1, r.bodyH - 3, c.tunicDark);
  }
}

function drawLegs(p: PixelBuf, r: Rig, c: Colors, pose: HeroPose): void {
  const lx = r.bodyX + 1;
  const rx = r.bodyX + r.bodyW - 4;
  const y = r.legY;
  const h = r.legH;
  const leg = (x: number, top: number, len: number, boot: boolean) => {
    p.rect(x, top, 3, len, c.tunicDark);
    p.rect(x, top, 1, len, c.outline);
    if (boot) {
      p.rect(x - 1, top + len - 3, 5, 3, c.boot);
      p.rect(x - 1, top + len - 1, 5, 1, c.bootDark);
    } else {
      p.rect(x, top + len - 1, 3, 1, c.bootDark);
    }
  };

  switch (pose) {
    case 'idle':
    case 'hurt':
    case 'duck':
      leg(lx, y, h, true);
      leg(rx, y, h, true);
      break;
    case 'walk0':
    case 'run0':
      leg(lx - 1, y, h - 1, true);
      leg(rx + 2, y - 1, h, true);
      break;
    case 'walk1':
    case 'run1':
      leg(lx, y - 1, h, true);
      leg(rx, y - 1, h - 1, true);
      break;
    case 'walk2':
    case 'run2':
      leg(lx + 2, y - 1, h, true);
      leg(rx - 1, y, h - 1, true);
      break;
    case 'jump':
      leg(lx - 1, y - 2, h, true);
      leg(rx + 1, y - 3, h - 1, true);
      break;
    case 'fall':
      leg(lx - 1, y - 1, h, true);
      leg(rx + 1, y - 1, h, true);
      break;
    case 'skid':
      leg(lx - 2, y, h, true);
      leg(rx + 2, y - 1, h - 1, true);
      break;
    case 'dead':
      leg(lx, y - 1, h, true);
      leg(rx, y - 1, h, true);
      break;
    case 'flag':
      leg(lx + 1, y, h, true);
      leg(rx - 1, y, h, true);
      break;
  }
}

function drawArmBack(p: PixelBuf, r: Rig, c: Colors, pose: HeroPose, lean: number): void {
  const x = r.bodyX + lean - 1;
  const y = r.armY;
  switch (pose) {
    case 'walk0':
    case 'run0':
      p.rect(x - 1, y + 1, 2, r.armH, c.tunicDark);
      break;
    case 'walk2':
    case 'run2':
      p.rect(x, y, 2, r.armH, c.tunicDark);
      break;
    case 'jump':
    case 'flag':
    case 'dead':
      p.rect(x, y - 2, 2, r.armH, c.tunicDark);
      break;
    case 'skid':
      p.rect(x - 2, y - 1, 3, 2, c.tunicDark);
      break;
    default:
      p.rect(x, y + 1, 2, r.armH - 1, c.tunicDark);
  }
}

function drawArmFront(
  p: PixelBuf,
  r: Rig,
  c: Colors,
  pose: HeroPose,
  lean: number,
  bob: number,
): void {
  const x = r.bodyX + r.bodyW + lean - 1;
  const y = r.armY + bob;
  const arm = (ax: number, ay: number, aw: number, ah: number) => {
    p.rect(ax, ay, aw, ah, c.tunic);
    p.rect(ax, ay + ah - 1, aw, 1, c.skin);
  };
  switch (pose) {
    case 'walk0':
    case 'run0':
      arm(x, y, 2, r.armH);
      break;
    case 'walk1':
    case 'run1':
      arm(x - 1, y + 1, 2, r.armH - 1);
      break;
    case 'walk2':
    case 'run2':
      arm(x + 1, y - 1, 2, r.armH);
      break;
    case 'jump':
      arm(x, y - 3, 2, r.armH);
      break;
    case 'fall':
      arm(x + 1, y, 2, r.armH - 1);
      break;
    case 'skid':
      arm(x + 1, y - 2, 3, 2);
      break;
    case 'hurt':
      arm(x - 1, y, 2, r.armH - 1);
      break;
    case 'dead':
      arm(x, y - 3, 2, r.armH);
      break;
    case 'flag':
      arm(x, y - 4, 2, r.armH + 2);
      break;
    default:
      arm(x, y + 1, 2, r.armH - 1);
  }
}

/** Draw offsets: art is slightly larger than the hitbox so the silhouette overhangs. */
export const HERO_OFFSET: Record<'small' | 'big', { x: number; y: number }> = {
  small: { x: -1, y: -1 },
  big: { x: -1, y: -1 },
};
